import { createSlice } from "@reduxjs/toolkit";
import {
  fetchLoginKind,
  fetchSystemConfig,
  impersonateUser,
  login,
  loginByCode,
  logout,
  refreshToken,
  register,
  stopImpersonation,
} from "../actions/authActions";
import {
  clearStoredAuth,
  readStoredUser,
  isAccessTokenExpired,
  persistAuthUser,
  getRefreshToken,
} from "../../infrastructure/utils/authSession";
import { resetMenuCache } from "../../infrastructure/utils/menuSession";

const storedUser = readStoredUser();
const hasValidAccessToken =
  storedUser?.accessToken && !isAccessTokenExpired(storedUser);
const hasRefreshToken = Boolean(getRefreshToken(storedUser));

if (storedUser && !hasValidAccessToken && !hasRefreshToken) {
  clearStoredAuth();
}

const user =
  storedUser && (hasValidAccessToken || hasRefreshToken) ? storedUser : null;
const storageSystemConfig = JSON.parse(localStorage.getItem("system-config"));

const loginKind = {
  loading: false,
  error: null,
  kind: null,
};

const systemConfig = {
  loading: false,
  error: null,
  data: storageSystemConfig || null,
};

const initialState = user
  ? { isLoggedIn: true, user, loginKind, systemConfig }
  : { isLoggedIn: false, user: null, loginKind, systemConfig };

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    resetAuthSession: (state) => {
      state.isLoggedIn = false;
      state.user = null;
      clearStoredAuth();
      resetMenuCache();
    },
    setIsConfirmedProfile: (state) => {
      if (state.user) {
        state.user.isConfirmed = true;
      }
    },
    setIsProfileCompleted: (state, action) => {
      if (state.user) {
        state.user.isProfileCompleted = action.payload;
      }
    },
  },
  extraReducers: {
    [register.fulfilled]: (state, action) => {
      state.isLoggedIn = true;
      state.user = action.payload?.user || null;
      if (action.payload?.user) {
        persistAuthUser(action.payload.user);
        resetMenuCache();
      }
    },
    [register.rejected]: (state, action) => {
      state.isLoggedIn = false;
    },
    [login.fulfilled]: (state, action) => {
      state.isLoggedIn = true;
      state.user = action.payload.user;
      if (action.payload.user) {
        persistAuthUser(action.payload.user);
        resetMenuCache();
      }
    },
    [login.rejected]: (state, action) => {
      state.isLoggedIn = false;
      state.user = null;
      // پاک کردن localStorage در صورت خطای لاگین
      localStorage.removeItem("user");
    },
    [logout.fulfilled]: (state, action) => {
      state.isLoggedIn = false;
      state.user = null;
      clearStoredAuth();
      resetMenuCache();
    },
    [refreshToken.fulfilled]: (state, action) => {
      state.isLoggedIn = true;
      state.user = action.payload.user;
      if (action.payload.user) {
        persistAuthUser(action.payload.user);
      }
    },
    [refreshToken.rejected]: (state, action) => {
      state.isLoggedIn = false;
      state.user = null;
      clearStoredAuth();
    },
    [loginByCode.fulfilled]: (state, action) => {
      state.isLoggedIn = true;
      state.user = action.payload.user;
      if (action.payload.user) {
        persistAuthUser(action.payload.user);
        resetMenuCache();
      }
    },
    [loginByCode.rejected]: (state, action) => {
      state.isLoggedIn = false;
      state.user = null;
      // پاک کردن localStorage در صورت خطای لاگین
      localStorage.removeItem("user");
    },
    [impersonateUser.fulfilled]: (state, action) => {
      state.isLoggedIn = true;
      state.user = action.payload.user;
      if (action.payload.user) {
        persistAuthUser(action.payload.user);
        resetMenuCache();
      }
    },
    [stopImpersonation.fulfilled]: (state, action) => {
      state.isLoggedIn = true;
      state.user = action.payload.user;
      if (action.payload.user) {
        persistAuthUser(action.payload.user);
        resetMenuCache();
      }
    },
    [fetchLoginKind.pending]: (state, action) => {
      state.loginKind.loading = true;
      state.loginKind.error = null;
    },
    [fetchLoginKind.fulfilled]: (state, action) => {
      state.loginKind.loading = false;
      state.loginKind.error = null;
      state.loginKind.kind = action.payload || "UserPass";
    },
    [fetchLoginKind.rejected]: (state, action) => {
      state.loginKind.loading = false;
      state.loginKind.error = action.error.message;
    },

    [fetchSystemConfig.pending]: (state, action) => {
      state.systemConfig.loading = true;
      state.systemConfig.error = null;
    },
    [fetchSystemConfig.fulfilled]: (state, action) => {
      state.systemConfig.loading = false;
      state.systemConfig.error = null;
      state.systemConfig.data = action.payload;
    },
    [fetchSystemConfig.rejected]: (state, action) => {
      state.systemConfig.loading = false;
      state.systemConfig.error = action.error.message;
    },
  },
});

const { reducer, actions } = authSlice;

export const { resetAuthSession, setIsConfirmedProfile, setIsProfileCompleted } = actions;

export default reducer;
